/**
 * Lightweight analytics shim.
 *
 * Posts events directly to the PostHog capture API via fetch — no SDK to
 * install, no global pollution. If `VITE_POSTHOG_KEY` is unset, every call is
 * a no-op so the same code runs unchanged in dev / preview.
 *
 * Usage:
 *   import { track, identify } from '../lib/analytics'
 *   track('caja_signup_completed', { industry, size })
 *   identify(userId, { email, plan })
 *
 * Why a fetch-based shim instead of `posthog-js`:
 *   • Zero added bundle size (~50KB saved)
 *   • No race-condition with React hydration
 *   • Easy to replace later if we outgrow it
 *
 * Auto-events captured:
 *   • $pageview on every navigation (call trackPageview() from App.jsx if you
 *     want this — currently opt-in)
 *   • All errors via `trackError(err, context)` from try/catch blocks
 */

const KEY  = import.meta.env.VITE_POSTHOG_KEY
const HOST = import.meta.env.VITE_POSTHOG_HOST || 'https://us.i.posthog.com'

let _distinctId = null
const _superProps = {}   // sent on every event

export function setUser(distinctId, props = {}) {
  _distinctId = distinctId ?? null
  Object.assign(_superProps, props)
}

export function identify(distinctId, props = {}) {
  if (!KEY || !distinctId) return
  _distinctId = distinctId
  fire('$identify', { $set: props })
}

export function track(event, props = {}) {
  if (!KEY) return
  fire(event, props)
}

export function trackError(err, context = {}) {
  if (!KEY) return
  fire('$exception', {
    $exception_message: err?.message ?? String(err),
    $exception_type:    err?.name ?? 'Error',
    $exception_stack_trace_raw: err?.stack ?? null,
    ...context,
  })
}

export function trackPageview(path = window.location.pathname) {
  if (!KEY) return
  fire('$pageview', { $current_url: window.location.href, path })
}

// ──────────────────────────────────────────────────────────────────────────────
function fire(event, props) {
  try {
    const body = JSON.stringify({
      api_key: KEY,
      event,
      distinct_id: _distinctId ?? anonymousId(),
      properties: { ..._superProps, ...props, $lib: 'nvc-shim', $lib_version: '1.0' },
      timestamp: new Date().toISOString(),
    })
    // sendBeacon is fire-and-forget and doesn't block navigation.
    if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      const blob = new Blob([body], { type: 'application/json' })
      navigator.sendBeacon(`${HOST}/i/v0/e/`, blob)
      return
    }
    fetch(`${HOST}/i/v0/e/`, {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
    }).catch(() => {/* swallow — analytics must never break the app */})
  } catch {/* same: swallow */}
}

function anonymousId() {
  try {
    const k = 'nvc-anon-id'
    let id = localStorage.getItem(k)
    if (!id) {
      id = 'anon_' + Math.random().toString(36).slice(2) + Date.now().toString(36)
      localStorage.setItem(k, id)
    }
    return id
  } catch {
    return 'anon_unknown'
  }
}
